import { useCallback, useState } from "react";
import { useSelector } from "react-redux";

import CONSUMER_SERVICES from "./consumerService";
import { getProfile, setProfile } from "./profileStore";
import {
  mergeIdentity,
  toIdentityPayload,
  toSavePayload,
  toUserProfile,
} from "./consumerMappers";
import { selectProfile } from "../../redux/reducers/user/user-Slice";
import { getApiErrorMessage } from "../../lib/api/apiError";

const unwrap = (body) => body?.data ?? body;

const useProfile = () => {
  const profile = useSelector(selectProfile);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const clearError = useCallback(() => setError(""), []);

  const fetchProfile = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await CONSUMER_SERVICES.fetchSelf();
      const row = unwrap(data);

      if (row) return setProfile(toUserProfile(row));
    } catch {
      /* keep the cached profile */
    } finally {
      setLoading(false);
    }

    return getProfile();
  }, []);

  const refreshIdentity = useCallback(async (base) => {
    try {
      const { data } = await CONSUMER_SERVICES.fetchIdentity();
      const identity = unwrap(data);

      if (identity) return setProfile(mergeIdentity(base, identity));
    } catch {
      /* the consumer write already landed */
    }

    return base;
  }, []);

  const saveProfile = useCallback(
    async (patch = {}) => {
      const current = getProfile();

      setSaving(true);
      setError("");

      try {
        const identityPayload = toIdentityPayload(current, patch);

        if (identityPayload) {
          await CONSUMER_SERVICES.patchIdentity(identityPayload);
        }

        const { data } = await CONSUMER_SERVICES.save(
          toSavePayload(current, patch)
        );

        const row = unwrap(data);
        const saved = setProfile(
          row ? toUserProfile(row) : { ...current, ...patch }
        );

        await refreshIdentity(saved);

        return true;
      } catch (err) {
        setError(getApiErrorMessage(err, "Could not save your profile"));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [refreshIdentity]
  );

  return {
    profile,
    loading,
    saving,
    error,
    clearError,
    fetchProfile,
    saveProfile,
  };
};

export default useProfile;
